import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2, PanelRightClose, PanelRightOpen, MessageCircle, Lightbulb, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLocalPref } from "@/v2/hooks/useLocalPref";
import { useDecisionEngine } from "@/v2/context/DecisionContext";
import { useNavigate } from "react-router-dom";

/*
  Floating AI Assistant + dockable side panel.
    - Bubble (default): bottom-right, click opens panel
    - Panel: right-side drawer, dockable via user preference (persisted)
    - History: persisted per session_id in localStorage
    - Backend: POST /api/command-center/copilot with { question, session_id }
    - Context-aware: seeds suggested prompts based on current pathname
*/

const SEED_PROMPTS = [
  "Summarize today's operations in 5 bullets",
  "Which SKUs need reordering this week?",
  "What's my worst-performing marketplace?",
  "Top 3 fires to fix right now",
  "Are any couriers underperforming?",
];

const PROMPT_BY_ROUTE = {
  "/orders":       ["Show orders at risk of SLA breach", "Which orders should I hold?", "Any COD spikes today?"],
  "/inventory":    ["Which SKUs will run out first?", "Dead stock over 90 days?", "Reorder recommendations"],
  "/finance":      ["Reconciliation gaps by marketplace", "Which claims should I chase?", "GST split this month"],
  "/couriers":     ["Which courier has best SLA today?", "RTO % by courier last 7d", "Recommend courier for tier-3 pincodes"],
  "/marketplaces": ["Which marketplace grew fastest?", "Failed syncs in last 24h", "Health score by channel"],
  "/products":     ["Top 10 products by margin", "Suggest listings I should optimize", "Any missing HSN codes?"],
};

export function AIAssistant() {
  const nav = useNavigate();
  const { recommendations, stats } = useDecisionEngine();
  const topInsights = recommendations.filter((r) => !r.isDismissed).slice(0, 3);
  const [open, setOpen] = useState(false);
  const [docked, setDocked] = useLocalPref("zj_v2_ai_docked", false);
  const [messages, setMessages] = useLocalPref("zj_v2_ai_history", []);
  const [sessionId] = useLocalPref("zj_v2_ai_session", () => `s-${Date.now().toString(36)}`);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const suggestions = (() => {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    for (const k of Object.keys(PROMPT_BY_ROUTE)) if (path.startsWith(k)) return PROMPT_BY_ROUTE[k];
    return SEED_PROMPTS;
  })();

  useEffect(() => {
    if (open) setTimeout(() => scrollRef.current?.scrollTo({ top: 9e9 }), 60);
  }, [open, messages]);

  const send = async (raw) => {
    const q = (raw ?? text).trim();
    if (!q || loading) return;
    setText("");
    const newMsgs = [...messages, { role: "user", text: q, at: Date.now() }];
    setMessages(newMsgs);
    setLoading(true);
    try {
      const { data } = await api.post("/command-center/copilot", { question: q, session_id: sessionId });
      const answer = data?.answer || data?.text || "I couldn't produce an answer just now.";
      setMessages([...newMsgs, { role: "assistant", text: answer, at: Date.now() }]);
    } catch (e) {
      setMessages([...newMsgs, { role: "assistant", text: "The AI is unreachable right now. Please try again in a moment.", at: Date.now(), error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => setMessages([]);

  return (
    <>
      {/* Bubble */}
      {!docked && (
        <button
          className="v2-ai-bubble"
          onClick={() => setOpen((v) => !v)}
          data-testid="v2-ai-bubble"
          title="AI Assistant"
          aria-label="AI Assistant"
        >
          {open ? <X size={20} /> : <Sparkles size={20} strokeWidth={2.2} />}
        </button>
      )}

      {/* Panel */}
      {(open || docked) && (
        <aside
          className="v2-ai-panel"
          data-testid="v2-ai-panel"
          style={docked ? { boxShadow: "none", animation: "none", position: "sticky", right: "auto", top: 0, height: "calc(100vh - 60px)" } : undefined}
        >
          <header style={{
            padding: "12px 16px", borderBottom: "1px solid var(--v2-border)",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: "var(--v2-grad-brand)", display: "grid", placeItems: "center", color: "#fff" }}>
                <Sparkles size={15} />
              </div>
              <div>
                <div style={{ fontFamily: "var(--v2-font-display)", fontWeight: 700, fontSize: 14, lineHeight: 1 }}>Jini AI</div>
                <div style={{ fontSize: 10.5, color: "var(--v2-text-muted)", marginTop: 3 }}>Claude Sonnet 4.5 · Context-aware</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button
                className="v2-btn v2-btn-ghost v2-btn-icon"
                onClick={() => setDocked((v) => !v)}
                title={docked ? "Undock" : "Dock"}
                style={{ width: 28, height: 28 }}
                data-testid="v2-ai-dock"
              >
                {docked ? <PanelRightClose size={13} /> : <PanelRightOpen size={13} />}
              </button>
              <button
                className="v2-btn v2-btn-ghost v2-btn-icon"
                onClick={() => setOpen(false)}
                title="Close"
                style={{ width: 28, height: 28 }}
                data-testid="v2-ai-close"
              >
                <X size={14} />
              </button>
            </div>
          </header>

          {/* Body */}
          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "14px 16px" }} data-testid="v2-ai-messages">
            {messages.length === 0 && (
              <div style={{ padding: "10px 0" }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Hi 👋 How can I help?</div>
                <div style={{ fontSize: 12.5, color: "var(--v2-text-muted)", lineHeight: 1.55 }}>
                  Ask about orders, stock, couriers, revenue, marketplace health, or anomalies. I read live data from your tenant only.
                </div>

                {topInsights.length > 0 && (
                  <div style={{ marginTop: 14 }} data-testid="v2-ai-top-insights">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                      <div className="v2-label-mono" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Lightbulb size={11} /> Top Insights
                      </div>
                      <button
                        className="v2-btn v2-btn-ghost v2-btn-sm"
                        onClick={() => { nav("/insights"); setOpen(false); }}
                        data-testid="v2-ai-see-all-insights"
                        style={{ padding: "3px 8px", fontSize: 11 }}
                      >
                        See all ({stats.active}) <ArrowRight size={10} />
                      </button>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {topInsights.map((r) => (
                        <button
                          key={r.id}
                          className="v2-panel"
                          style={{
                            textAlign: "left", padding: "8px 10px", cursor: "pointer",
                            display: "flex", flexDirection: "column", gap: 3,
                          }}
                          onClick={() => { if (r.destination) nav(r.destination); setOpen(false); }}
                          data-testid={`v2-ai-insight-${r.id}`}
                        >
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <span className={`v2-chip ${r.priority === "urgent" ? "v2-chip-danger" : r.priority === "high" ? "v2-chip-warning" : "v2-chip-info"}`} style={{ fontSize: 9 }}>
                              {r.priority}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</span>
                          </div>
                          <div style={{ fontSize: 10.5, color: "var(--v2-text-muted)" }}>{r.confidence}% conf · {r.category}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 6 }}>
                  <div className="v2-label-mono">Suggested Prompts</div>
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      className="v2-btn v2-btn-ghost"
                      onClick={() => send(s)}
                      data-testid={`v2-ai-seed-${s.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`}
                      style={{ justifyContent: "flex-start", fontSize: 12.5, fontWeight: 500 }}
                    >
                      <MessageCircle size={12} />
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`v2-ai-msg-${m.role}`}
                style={{
                  marginBottom: 10,
                  display: "flex",
                  justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                }}
              >
                <div style={{
                  maxWidth: "88%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  fontSize: 13,
                  lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                  background: m.role === "user"
                    ? "var(--v2-grad-brand)"
                    : (m.error ? "var(--v2-danger-soft)" : "var(--v2-surface-hover)"),
                  color: m.role === "user" ? "#fff" : (m.error ? "#FCA5A5" : "var(--v2-text)"),
                  border: m.role === "user" ? "0" : "1px solid var(--v2-border)",
                }}>
                  {m.text}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--v2-text-muted)", fontSize: 12 }} data-testid="v2-ai-thinking">
                <Loader2 size={13} style={{ animation: "spin 900ms linear infinite" }} />
                Jini is thinking…
              </div>
            )}
          </div>

          {/* Composer */}
          <div style={{ padding: 12, borderTop: "1px solid var(--v2-border)" }}>
            <div style={{ display: "flex", gap: 8 }}>
              <textarea
                className="v2-textarea"
                placeholder="Ask Jini about orders, inventory, revenue…"
                value={text}
                rows={2}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                data-testid="v2-ai-input"
                style={{ resize: "none", fontSize: 13 }}
              />
              <button
                className="v2-btn v2-btn-primary v2-btn-icon"
                onClick={() => send()}
                disabled={loading || !text.trim()}
                data-testid="v2-ai-send"
                style={{ alignSelf: "stretch", height: "auto", minWidth: 38 }}
                aria-label="Send"
              >
                {loading ? <Loader2 size={14} style={{ animation: "spin 900ms linear infinite" }} /> : <Send size={14} />}
              </button>
            </div>
            {messages.length > 0 && (
              <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 10.5, color: "var(--v2-text-muted)" }}>Enter to send · Shift+Enter for line break</div>
                <button className="v2-btn v2-btn-ghost v2-btn-sm" onClick={clear} data-testid="v2-ai-clear">Clear</button>
              </div>
            )}
          </div>
        </aside>
      )}
    </>
  );
}
